
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/components/AuthProvider";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { isValidName, validateUserInput } from "@/utils/security";

// Enhanced schema with custom validators for name fields
const profileFormSchema = z.object({
  firstName: z
    .string()
    .min(2, "First name must be at least 2 characters.")
    .refine(val => isValidName(val), {
      message: "First name contains invalid characters",
    }),
  lastName: z
    .string()
    .min(2, "Last name must be at least 2 characters.")
    .refine(val => isValidName(val), {
      message: "Last name contains invalid characters",
    }),
  email: z.string().email("Invalid email address.").min(1, "Email is required"),
});

export function ProfileForm() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user?.id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const updateProfile = useMutation({
    mutationFn: async (values: z.infer<typeof profileFormSchema>) => {
      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: values.firstName,
          last_name: values.lastName,
        })
        .eq('id', user?.id);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Profile updated successfully");
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
    onError: (error) => {
      toast.error("Failed to update profile");
      console.error('Error updating profile:', error);
    },
  });

  const form = useForm<z.infer<typeof profileFormSchema>>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      firstName: profile?.first_name || "",
      lastName: profile?.last_name || "",
      email: user?.email || "",
    },
  });

  // Handle input validation with notifications
  const handleInputChange = (field: keyof z.infer<typeof profileFormSchema>, value: string) => {
    const validation = validateUserInput(value);
    if (!validation.isValid && validation.message) {
      toast.error(validation.message);
      return false;
    }
    return true;
  };

  function onSubmit(values: z.infer<typeof profileFormSchema>) {
    // Final validation before submission
    const firstNameValidation = validateUserInput(values.firstName);
    if (!firstNameValidation.isValid) {
      toast.error(firstNameValidation.message || "Invalid first name input");
      return;
    }
    
    const lastNameValidation = validateUserInput(values.lastName);
    if (!lastNameValidation.isValid) {
      toast.error(lastNameValidation.message || "Invalid last name input");
      return;
    }
    
    updateProfile.mutate(values);
  }

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="firstName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>First Name</FormLabel>
              <FormControl>
                <Input 
                  placeholder="John" 
                  {...field} 
                  onChange={(e) => {
                    const isValid = handleInputChange('firstName', e.target.value);
                    if (isValid) field.onChange(e);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Last Name</FormLabel>
              <FormControl>
                <Input 
                  placeholder="Doe" 
                  {...field} 
                  onChange={(e) => {
                    const isValid = handleInputChange('lastName', e.target.value);
                    if (isValid) field.onChange(e);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input type="email" disabled {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" className="w-full bg-black hover:bg-black/90">
          Update Profile
        </Button>
      </form>
    </Form>
  );
}
